import React from 'react';
import { Icon } from '@iconify-icon/react';
import { Button } from '@mantine/core';
import Link from 'next/link';

import {
  ContactUsFormFields,
  useContactUsFormController,
} from '@/components/modules/Landing/ContactUsForm';
import { CONTACT_US_TYPES } from '@/types/entities/contact.entities';
import { cn } from '@/utils/helper';
import PageLink from '@/utils/PageLink';

export type { ContactUsSchemaFormData } from '@/components/modules/Landing/ContactUsForm';

interface GetInTouchProps {
  isFormVisible?: boolean;
  isDarkMode?: boolean;
  isSupportForm?: boolean;
}

/** The marketing contact page (/contact-us): hero, other ways to reach us, and the shared contact form. */
const GetInTouch: React.FC<GetInTouchProps> = ({
  isFormVisible = true,
  isDarkMode = false,
  isSupportForm = false,
}) => {
  // Hardcoded content — CMS data previously overrode redesigned text (same fix as Hero)
  const memoizedGetInTouchContentData = {
    title: "Have Questions? Let's Talk!",
    description:
      "We're here to help! Whether you have inquiries about our chatbot solutions, need support, or want a custom plan, please reach out to us",
  };
  // Owned here, not by the fields: hiding the form (isFormVisible) keeps its values, token and success state.
  const contactForm = useContactUsFormController({
    type: isSupportForm ? CONTACT_US_TYPES.SUPPORT : CONTACT_US_TYPES.CONTACT,
  });

  /* ── Contact info cards shown on the left panel ── */
  const contactCards = [
    {
      icon: 'solar:letter-bold',
      label: 'Email us',
      value: 'hello@chat.co',
      href: 'mailto:hello@chat.co',
    },
    {
      icon: 'solar:phone-calling-bold',
      label: 'Call us',
      value: '+1 (415) 319-9443',
      href: 'tel:+14153199443',
    },
    {
      icon: 'solar:clock-circle-bold',
      label: 'Response time',
      value: 'Within 24 hours',
      href: null,
    },
  ];

  return (
    <main className="w-full" id="contact-us">
      {/* ─── Hero banner ─── */}
      <section
        className={cn('relative overflow-hidden', {
          'bg-[#0b1120]': isDarkMode,
          'bg-[#0d1b3e]': !isDarkMode,
        })}>
        {/* Subtle grid pattern */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.15) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        {/* Gold accent glow */}
        <div
          className="pointer-events-none absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full opacity-20 blur-[120px]"
          style={{ background: 'radial-gradient(circle, #d4a843 0%, transparent 70%)' }}
        />

        <div className="relative mx-auto max-w-5xl px-6 pb-14 pt-20 text-center sm:pb-20 sm:pt-28">
          <p
            className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-2 text-xs font-medium uppercase tracking-[0.2em] text-gray-300 backdrop-blur-sm"
            style={{ animationDelay: '0.1s' }}>
            <Icon icon="solar:chat-round-call-bold" className="text-sm text-[#d4a843]" />
            Get in Touch
          </p>
          <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
            {memoizedGetInTouchContentData.title}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-gray-400 sm:text-lg">
            {memoizedGetInTouchContentData.description}
          </p>
        </div>
      </section>

      {/* ─── Main content area ─── */}
      <section
        className={cn('relative', {
          'bg-[#0f172a]': isDarkMode,
          'bg-gray-50': !isDarkMode,
        })}>
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:py-20">
          {isFormVisible && (
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-5 lg:gap-16">
              {/* ── Left column: contact info ── */}
              <div className="lg:col-span-2">
                <div className="sticky top-28">
                  <h2
                    className={cn('text-xl font-bold sm:text-2xl', {
                      'text-white': isDarkMode,
                      'text-gray-900': !isDarkMode,
                    })}>
                    Other ways to reach us
                  </h2>
                  <p
                    className={cn('mt-3 text-sm leading-relaxed', {
                      'text-gray-400': isDarkMode,
                      'text-gray-500': !isDarkMode,
                    })}>
                    Prefer a direct line? Use any of the options below. Our team typically responds
                    within one business day.
                  </p>

                  <div className="mt-8 space-y-4">
                    {contactCards.map((card) => {
                      const Wrapper = card.href ? 'a' : 'div';
                      return (
                        <Wrapper
                          key={card.label}
                          {...(card.href ? { href: card.href } : {})}
                          className={cn(
                            'group flex items-start gap-4 rounded-2xl border p-5 transition-all duration-200',
                            {
                              'border-gray-700/60 bg-gray-800/40 hover:border-gray-600 hover:bg-gray-800/70':
                                isDarkMode,
                              'border-gray-200 bg-white shadow-sm hover:border-[#0d1b3e]/20 hover:shadow-md':
                                !isDarkMode,
                            }
                          )}>
                          <span
                            className={cn(
                              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg',
                              {
                                'bg-[#d4a843]/10 text-[#d4a843]': true,
                              }
                            )}>
                            <Icon icon={card.icon} />
                          </span>
                          <div>
                            <p
                              className={cn('text-xs font-medium uppercase tracking-wider', {
                                'text-gray-500': isDarkMode,
                                'text-gray-400': !isDarkMode,
                              })}>
                              {card.label}
                            </p>
                            <p
                              className={cn('mt-1 text-sm font-semibold', {
                                'text-gray-100': isDarkMode,
                                'text-gray-900': !isDarkMode,
                              })}>
                              {card.value}
                            </p>
                          </div>
                        </Wrapper>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* ── Right column: form card ── */}
              <div className="lg:col-span-3">
                <div
                  className={cn('rounded-3xl border p-6 sm:p-8 lg:p-10', {
                    'border-gray-700/60 bg-gray-800/50': isDarkMode,
                    'border-gray-200 bg-white shadow-lg shadow-gray-200/50': !isDarkMode,
                  })}>
                  <h3
                    className={cn('text-lg font-bold', {
                      'text-white': isDarkMode,
                      'text-gray-900': !isDarkMode,
                    })}>
                    Send us a message
                  </h3>
                  <p
                    className={cn('mt-1 text-sm', {
                      'text-gray-400': isDarkMode,
                      'text-gray-500': !isDarkMode,
                    })}>
                    Fill out the form and we&rsquo;ll get back to you promptly.
                  </p>

                  <div className="mt-6">
                    <ContactUsFormFields controller={contactForm} isDarkMode={isDarkMode} />
                  </div>
                </div>
              </div>
            </div>
          )}
          {!isFormVisible && (
            <div className="mt-10 flex justify-center">
              <Button
                size="md"
                className="w-fit bg-white text-sm font-medium text-gray-900"
                component={Link}
                href={PageLink.contactUs}
                rightSection={
                  <Icon icon="tabler:chevron-right" className="text-xl text-gray-900" />
                }>
                Contact us
              </Button>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default GetInTouch;
